import { useCallback, useEffect, useState } from "react";
import { Pencil, Trash2, Clock3, Undo2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { fmtQty } from "@/lib/wmDigits";
import { wmMessage, WM_TIMEOUT_MS } from "@/lib/wmError";
import { QuantityInput } from "@/components/waste-meals/QuantityInput";
import { NOTES_MAX, countsAsWaste, isNonLossReason } from "@/components/waste-meals/wmUtils";

const C = OPS.wm;

const labelOf = (list, key, lang) => {
    const hit = (list || []).find((x) => x.key === key);
    if (!hit) return key || "";
    return lang === "ar" ? hit.name_ar : hit.name_en || hit.name_ar;
};

/** Correct a record inside the window: quantity, reason and note only. */
export function RecordEditDialog({ open, onOpenChange, record, catalogue, onSaved }) {
    const { t, lang } = useI18n();
    const [qty, setQty] = useState("");
    const [reason, setReason] = useState("");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open && record) {
            setQty(String(record.quantity ?? ""));
            setReason(record.reason_key || "");
            setNotes(record.notes || "");
        }
    }, [open, record]);

    if (!record) return null;
    const unit = (catalogue?.units || []).find((u) => u.key === record.unit_key);

    const save = async () => {
        setBusy(true);
        try {
            await api.patch(`/waste-meals/records/${record.id}`, {
                quantity: qty,
                reason_key: record.type === "waste" ? (reason || null) : null,
                notes,
            }, { timeout: WM_TIMEOUT_MS });
            toast.success(t("wm_saved"));
            onOpenChange(false);
            onSaved && (await onSaved());
        } catch (e) {
            toast.error(wmMessage(e, lang) || t("wm_offline_error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid={C.editDialog.dialog} className="max-w-sm">
                <DialogHeader>
                    <DialogTitle className="text-base">
                        {lang === "ar" ? record.item_name_ar : record.item_name_en || record.item_name_ar}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <QuantityInput testId={C.editDialog.qty} value={qty} onChange={setQty}
                                   unitLabel={unit ? (lang === "ar" ? unit.name_ar : unit.name_en) : record.unit_key}
                                   allowsDecimal={!!unit?.allows_decimal} autoFocus />
                    {record.type === "waste" ? (
                        <Select value={reason} onValueChange={setReason}>
                            <SelectTrigger data-testid={C.editDialog.reason} className="h-10">
                                <SelectValue placeholder={t("wm_reason")} />
                            </SelectTrigger>
                            <SelectContent>
                                {(catalogue?.reasons || []).map((r) => (
                                    <SelectItem key={r.key} value={r.key}>
                                        {lang === "ar" ? r.name_ar : r.name_en}
                                        {!countsAsWaste(r) ? (
                                            <span className="ms-1.5 text-[10px] font-semibold text-[color:var(--brand-brown)]">
                                                · {t("wm_reason_not_waste")}
                                            </span>
                                        ) : null}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : null}
                    <Textarea data-testid={C.editDialog.notes} rows={2} value={notes} maxLength={NOTES_MAX}
                              onChange={(e) => setNotes(e.target.value)} placeholder={t("wm_notes")}
                              className="text-base" />
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("wm_cancel")}</Button>
                    <Button data-testid={C.editDialog.save} onClick={save} disabled={busy}>
                        {busy ? t("wm_saving") : t("wm_save")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * The record list used by BOTH the day-sheet chip and a grid cell click.
 * Edit / delete render ONLY where the server-computed `can_edit` /
 * `can_delete` allow it.
 */
export function RecordList({ rows, catalogue, testIdRow, testIdEdit, testIdDelete, testIdReturn, onChanged }) {
    const { t, lang } = useI18n();
    const [editing, setEditing] = useState(null);

    const remove = async (rec) => {
        if (!window.confirm(t("wm_delete_confirm"))) return;
        try {
            await api.delete(`/waste-meals/records/${rec.id}`, { timeout: WM_TIMEOUT_MS });
            toast.success(t("wm_deleted"));
            onChanged && (await onChanged());
        } catch (e) {
            toast.error(wmMessage(e, lang) || t("wm_offline_error"));
        }
    };

    return (
        <>
            <ul className="divide-y divide-[color:var(--separator)]">
                {rows.map((r) => (
                    <li key={r.id} data-testid={testIdRow ? testIdRow(r.id) : undefined}
                        className="flex items-start gap-3 py-2.5">
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <bdi dir="ltr" className="text-sm font-bold tabular-nums text-[color:var(--ink)]">
                                    {fmtQty(r.quantity, lang)} {labelOf(catalogue?.units, r.unit_key, lang)}
                                </bdi>
                                <span className="truncate text-sm text-[color:var(--ink-muted)]">
                                    {lang === "ar" ? r.item_name_ar : r.item_name_en || r.item_name_ar}
                                </span>
                                <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                                    {t(`wm_source_${r.source || "quick"}`)}
                                </Badge>
                                {r.deleted_at ? (
                                    <Badge variant="destructive" className="px-1.5 py-0 text-[10px]">
                                        {t("wm_delete")}
                                    </Badge>
                                ) : null}
                                {/* a return to the warehouse is listed here (the cell
                                    behind it excludes it), so it must read as one */}
                                {r.type !== "meals" && isNonLossReason(catalogue?.reasons, r.reason_key) ? (
                                    <Badge data-testid={testIdReturn ? testIdReturn(r.id) : undefined} variant="brown"
                                           className="gap-1 px-1.5 py-0 text-[10px]">
                                        <Undo2 className="h-3 w-3" />
                                        {t("wm_reason_not_waste")}
                                    </Badge>
                                ) : null}
                            </div>
                            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-[color:var(--ink-faint)]">
                                {r.reason_key ? <span>{labelOf(catalogue?.reasons, r.reason_key, lang)}</span> : null}
                                {r.notes ? <span className="truncate">{r.notes}</span> : null}
                                <span className="inline-flex items-center gap-1">
                                    <Clock3 className="h-3 w-3" />
                                    <bdi dir="ltr">{(r.created_at || "").slice(11, 16)}</bdi>
                                </span>
                                <span>{t("wm_by")} {r.created_by_name}</span>
                            </p>
                        </div>
                        <div className="flex shrink-0 gap-1">
                            {r.can_edit ? (
                                <Button size="icon" variant="ghost" className="h-8 w-8"
                                        data-testid={testIdEdit ? testIdEdit(r.id) : undefined}
                                        onClick={() => setEditing(r)}>
                                    <Pencil className="h-3.5 w-3.5" />
                                </Button>
                            ) : null}
                            {r.can_delete ? (
                                <Button size="icon" variant="ghost"
                                        className="h-8 w-8 text-[color:var(--danger)]"
                                        data-testid={testIdDelete ? testIdDelete(r.id) : undefined}
                                        onClick={() => remove(r)}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            ) : null}
                        </div>
                    </li>
                ))}
            </ul>
            <RecordEditDialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}
                              record={editing} catalogue={catalogue} onSaved={onChanged} />
        </>
    );
}

/**
 * RecordPanel — opened from a day-sheet chip AND from a single-branch grid
 * cell. Under append a cell is the sum of several records, so a supervisor
 * looking at 12.4 can see exactly why.
 */
export function RecordPanel({ open, onOpenChange, branchId, date, type, itemId, itemName, branchCount, onChanged }) {
    const { t, lang } = useI18n();
    const [rows, setRows] = useState([]);
    const [scope, setScope] = useState("branch");
    const [loading, setLoading] = useState(false);
    const [catalogue, setCatalogue] = useState(null);

    const load = useCallback(async () => {
        if (!open || !branchId || !itemId) return;
        setLoading(true);
        try {
            const [recs, cat] = await Promise.all([
                api.get("/waste-meals/records", {
                    params: { branch_id: branchId, date, type, item_id: itemId, page_size: 200 },
                }),
                api.get("/waste-meals/catalogue", { params: { type, branch_id: branchId } }),
            ]);
            setRows(recs.data.rows || []);
            setScope(recs.data.scope);
            setCatalogue(cat.data);
        } catch (e) {
            toast.error(wmMessage(e, lang) || t("wm_offline_error"));
        } finally {
            setLoading(false);
        }
    }, [open, branchId, date, type, itemId, lang, t]);

    useEffect(() => { load(); }, [load]);

    const hidden = scope === "own" && typeof branchCount === "number"
        ? Math.max(0, branchCount - rows.length) : 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid={C.panel.dialog} className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="text-base">{itemName}</DialogTitle>
                </DialogHeader>
                <p className="text-xs text-[color:var(--ink-muted)]">
                    <bdi dir="ltr">{date}</bdi>
                    {scope === "own" ? ` · ${t("wm_scope_own")}` : ""}
                </p>
                {loading ? <InlineLoader /> : rows.length ? (
                    <RecordList rows={rows} catalogue={catalogue} testIdRow={C.panel.row}
                                testIdEdit={C.panel.edit} testIdDelete={C.panel.del}
                                testIdReturn={C.panel.returnBadge}
                                onChanged={async () => { await load(); onChanged && (await onChanged()); }} />
                ) : (
                    <p data-testid={C.panel.empty} className="py-6 text-center text-sm text-[color:var(--ink-muted)]">
                        {t("wm_my_entries_empty")}
                    </p>
                )}
                {hidden > 0 ? (
                    <p data-testid={C.panel.hidden} className="text-[11px] text-[color:var(--ink-faint)]">
                        <bdi dir="ltr">{hidden}</bdi> {t("wm_hidden_colleague_records")}
                    </p>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}
